import React, { useState, useEffect, createContext, useContext } from "react";
import Cookies, { CookieAttributes } from "js-cookie";

interface User {
  username: string;
  email: string;
  expireAt: string;
}

interface AuthState {
  token: string | null;
  user?: User | null;
  loaded: boolean;
}

interface AuthContextType {
  auth: AuthState;
  storeAuth: (token: string, user: User) => void;
  clearAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [auth, setAuth] = useState<AuthState>({
    token: null,
    user: null,
    loaded: false,
  });

  useEffect(() => {
    const token = Cookies.get("auth_token");
    const user_data = Cookies.get("auth_user");

    if (token && user_data) {
      try {
        const user: User = JSON.parse(user_data);
        setAuth({ token, user, loaded: true });
      } catch (error) {
        // console.error("Failed to parse user from cookie:", error);
        setAuth({ token: null, user: null, loaded: true });
      }
    } else {
      // console.log("No auth cookie found");
      setAuth({ token: null, user: null, loaded: true });
    }
  }, []);

  const storeAuth = (token: string, user: User) => {
    const isProd = process.env.NODE_ENV === "production";
    const cookieDomain = isProd ? ".acfautomation.com" : undefined;

    const cookieOptions: CookieAttributes = {
      domain: cookieDomain,
      secure: isProd,
      sameSite: isProd ? "None" : "Lax",
      expires: 7,
    };

    Cookies.set("auth_token", token, cookieOptions);
    Cookies.set("auth_user", JSON.stringify(user), cookieOptions);
    setAuth({ token, user, loaded: true });
  };

  const clearAuth = () => {
    const isProd = process.env.NODE_ENV === "production";
    const cookieDomain = isProd ? ".acfautomation.com" : undefined;

    Cookies.remove("auth_token", { domain: cookieDomain });
    Cookies.remove("auth_user", { domain: cookieDomain });

    setAuth({ token: null, user: null, loaded: true });
  };

  return (
    <AuthContext.Provider value={{ auth, storeAuth, clearAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
